/**
 * Same structure as schema config: defines what a plan can/cannot do.
 * Used by AdminPlanConfig to build plan.config via form (no raw JSON).
 *
 * Canonical section order (same in plan, plan_license, subscription, license_type):
 * 1) Setup (show setup) — full width
 * 2) Setup Configuration (when setup on): Setup Menu | Tab → Field | Layout → Workflow | Scoring Rules →
 *    Assignment Rules | Web Forms → Search Views | Lead Conversion → Object Manager (single) → Object Permissions (full)
 * 3) Report | Dashboard | Storage (common; not part of Setup)
 * Use Row g-3 and Card p-3 for consistent spacing; Form.Group mb-2 and mb-0 mt-2 for "Objects allowed for" dropdowns.
 */

export const BYTES_PER_GB = 1024 ** 3;

/** Default data storage cap (20 GB). Matches STORAGE_DEFAULT_DATA_LIMIT_BYTES. */
export const DEFAULT_DATA_STORAGE_BYTES = 20 * BYTES_PER_GB;

/** Default file storage cap (100 GB). Matches STORAGE_DEFAULT_FILE_LIMIT_BYTES. */
export const DEFAULT_FILE_STORAGE_BYTES = 100 * BYTES_PER_GB;

/** Display bytes as GB for admin inputs. -1 stays -1 (unlimited). */
export function storageBytesToGbInput(bytes) {
  const n = Number(bytes);
  if (n === -1) return -1;
  if (!Number.isFinite(n) || n <= 0) return '';
  return Math.round((n / BYTES_PER_GB) * 100) / 100;
}

/** Parse admin GB input to bytes. -1 = unlimited. */
export function parseStorageGbInput(gbValue) {
  if (gbValue === '' || gbValue == null) return null;
  const n = Number(gbValue);
  if (!Number.isFinite(n)) return null;
  if (n === -1) return -1;
  if (n < 0) return null;
  return Math.round(n * BYTES_PER_GB);
}

export const DEFAULT_PLAN_CONFIG = {
  /** Which objects the user can access (tabs, object manager, lead conversion). Single source of truth. */
  allowedObjectsPermission: ['account', 'contact', 'opportunity', 'lead', 'order', 'activity', 'attachment'],
  /** Package keys enabled for this plan (e.g. ['watconnect']). Standard objects with config.package in this list are shown. */
  enabledPackages: [],
  app: {
    maxApps: -1,
    allowCreateApp: true,
  },
  tab: {
    maxTabs: 5,
    allowCreate: true,
    /** Standard tab ids to allow. When empty, all tabs matching allowedObjectsPermission (object_name) are selected by default. */
    allowedTabs: [],
  },
  field: {
    maxCustom: 50,
    allowCreate: false,
    allowStandardEdit: true,
    allowStandardDelete: false,
    allowEditLabel: true,
  },
  layout: {
    countMode: 'pair',
    maxLayouts: 1,
    allowCreate: true,
  },
  report: {
    maxReports: 10,
    allowCreateReport: true,
  },
  dashboard: {
    maxDashboards: 5,
    maxAssignedDashboardsPerUser: 2,
    allowCreateDashboard: true,
  },
  /** Tenant-wide storage caps (bytes). Subscription config may override plan defaults. */
  storage: {
    dataLimitBytes: DEFAULT_DATA_STORAGE_BYTES,
    fileLimitBytes: DEFAULT_FILE_STORAGE_BYTES,
  },
  workflow: {
    maxWorkflows: 10,
    allowViewWorkflow: true,
    allowCreateWorkflow: true,
  },
  scoringRule: {
    allowViewScoringRule: true,
    allowCreateScoringRule: true,
    /** Objects on which user can create scoring rules. Only objects in Allowed Objects Permission are shown in the dropdown. */
    allowedObjects: [],
  },
  assignmentRule: {
    allowViewAssignmentRule: true,
    allowCreateAssignmentRule: true,
    /** Objects on which user can create assignment rules. Only objects in Allowed Objects Permission are shown in the dropdown. */
    allowedObjects: [],
  },
  webform: {
    allowViewWebform: true,
    allowCreateWebform: true,
    /** Objects on which user can create web forms. Only objects in Allowed Objects Permission are shown in the dropdown. */
    allowedObjects: [],
  },
  searchView: {
    allowViewSearchView: true,
    allowCreateSearchView: true,
    /** Objects on which user can configure search views. Only objects in Allowed Objects Permission are listed. */
    allowedObjects: [],
  },
  migration: {
    allow: false,
  },
  objectManager: {
    mode: 'allowlist',
    scope: 'standard',
    allow_create: true,
    enable_standard: true,
    allowedObjects: ['account', 'contact', 'opportunity', 'lead', 'order', 'activity', 'attachment'],
  },
  leadConversionMapping: {
    mode: 'allowlist',
    allow: true,
    allowCustomObject: true,
    scope: 'standard',
    maxObjects: 5,
    allowedObjects: ['account', 'contact', 'opportunity'],
  },
  setup: {
    showSetup: true,
    mode: 'allowlist',
    allowedItems: [
      'objectmanager',
      'rolemanager',
      'tabs',
      'appmanager',
      'actions',
      'custom-scripts',
      'workflows',
      'homewidget',
      'webforms',
      'scoringrule',
      'object-health',
      'usergroups',
      'users',
      'mailconfig',
      'emailtemplate',
      'emaillayout',
      'assignmentrule',
      'searchviews',
      'session_setting',
      'scheduled-jobs',
      'stage_master',
      'aichatbot',
    ],
  },
  /** Per-object permissions (Create, Read, Update, Delete, View All, Modify All). Same UI as role PermissionBulkEdit. */
  objectPermissions: {},
};

/** Deduplicate enabled package keys (case-insensitive; keeps first spelling). */
export function dedupeEnabledPackages(packages) {
  if (!Array.isArray(packages)) return [];
  const seen = new Set();
  const out = [];
  for (const p of packages) {
    const l = String(p || '').toLowerCase().trim();
    if (!l || seen.has(l)) continue;
    seen.add(l);
    out.push(String(p).trim());
  }
  return out;
}

/**
 * Toggle a package in enabledPackages (add/remove; multiple CTI packages may be on).
 * @param {string[]} currentPackages
 * @param {string} packageKey
 * @param {boolean} checked
 */
export function applyTelephonyPackageToggle(currentPackages, packageKey, checked) {
  const keyLc = String(packageKey || '').toLowerCase().trim();
  const base = Array.isArray(currentPackages) ? currentPackages : [];
  if (!keyLc) return dedupeEnabledPackages(base);
  if (!checked) {
    return base.filter((p) => String(p).toLowerCase().trim() !== keyLc);
  }
  const seen = new Set(base.map((p) => String(p).toLowerCase().trim()));
  if (seen.has(keyLc)) return base;
  return dedupeEnabledPackages([...base, packageKey]);
}

/** Must match company_subscription / plan enabledPackages and public.standard_object.config.package. */
export const VISIT_MANAGER_PACKAGE_KEY = 'visit_manager';

/**
 * Visit Manager object API names (must match public.standard_object.object_name).
 * Kept in sync with ibs-spark-backend/app/models/admin.company.model.js VISIT_MANAGER_STANDARD_OBJECT_NAMES.
 */
export const VISIT_MANAGER_PACKAGE_OBJECT_NAMES = Object.freeze([
  'visit_plan',
  'visit_plan_template',
  'visit_stop',
  'visit_stop_template',
  'visit_template',
]);

export function isVisitManagerPackageOn(enabledPackages) {
  return (enabledPackages || []).some(
    p => String(p || '').toLowerCase().trim() === VISIT_MANAGER_PACKAGE_KEY
  );
}

/**
 * When Visit Manager is not enabled, remove its object keys from an allowed-objects list (UI + persisted config).
 */
export function stripVisitManagerObjectsIfPackageOff(enabledPackages, allowedList) {
  if (!Array.isArray(allowedList)) return [];
  if (isVisitManagerPackageOn(enabledPackages)) return [...allowedList];
  const deny = new Set(VISIT_MANAGER_PACKAGE_OBJECT_NAMES.map(n => n.toLowerCase()));
  return allowedList.filter(k => !deny.has(String(k || '').toLowerCase().trim()));
}

/** Must match company_subscription / plan enabledPackages and public.standard_object.config.package. */
export const REAL_ESTATE_PACKAGE_KEY = 'real_estate';

/**
 * Real Estate object API names (must match public.standard_object.object_name).
 * Contract is core CRM (not package-gated). Kept in sync with admin.company.model.js.
 */
export const REAL_ESTATE_PACKAGE_OBJECT_NAMES = Object.freeze([
  'property',
  'floor',
  'unit',
  'property_interest',
]);

export function isRealEstatePackageOn(enabledPackages) {
  return (enabledPackages || []).some(
    p => String(p || '').toLowerCase().trim() === REAL_ESTATE_PACKAGE_KEY
  );
}

/**
 * When Real Estate is not enabled, remove its object keys from an allowed-objects list.
 */
export function stripRealEstateObjectsIfPackageOff(enabledPackages, allowedList) {
  if (!Array.isArray(allowedList)) return [];
  if (isRealEstatePackageOn(enabledPackages)) return [...allowedList];
  const deny = new Set(REAL_ESTATE_PACKAGE_OBJECT_NAMES.map(n => n.toLowerCase()));
  return allowedList.filter(k => !deny.has(String(k || '').toLowerCase().trim()));
}

/** Object names to hide from admin allowed-object picklists when their package is off. */
export function getDisabledPackageObjectDenySet(enabledPackages) {
  const deny = new Set();
  if (!isVisitManagerPackageOn(enabledPackages)) {
    VISIT_MANAGER_PACKAGE_OBJECT_NAMES.forEach(n => deny.add(n.toLowerCase()));
  }
  if (!isRealEstatePackageOn(enabledPackages)) {
    REAL_ESTATE_PACKAGE_OBJECT_NAMES.forEach(n => deny.add(n.toLowerCase()));
  }
  return deny;
}

export function stripDisabledPackageObjects(enabledPackages, allowedList) {
  return stripRealEstateObjectsIfPackageOff(
    enabledPackages,
    stripVisitManagerObjectsIfPackageOff(enabledPackages, allowedList)
  );
}

/** WatConnect Lead Ads settings object (optional within watconnect package). */
export const WATCONNECT_ADS_SETTING_OBJECT_NAME = 'watconnect_ads_setting';
/** @deprecated use WATCONNECT_ADS_SETTING_OBJECT_NAME */
export const WATCONNECT_ADS_SETTINGS_OBJECT_NAME = WATCONNECT_ADS_SETTING_OBJECT_NAME;

/**
 * When false, object is visible only if its package is enabled but is not auto-selected or locked
 * when that package is turned on (admin must pick it in Allowed Objects).
 */
export function isStandardObjectPackageRequired(standardObject) {
  const cfg = standardObject?.config;
  if (cfg == null || typeof cfg !== 'object') return true;
  return cfg.package_required !== false;
}

/** Package key on a standard_tab row (top-level or nested config.config). */
export function getStandardTabPackageKey(tab) {
  const cfg = tab?.config;
  if (cfg == null || typeof cfg !== 'object') return '';
  const inner = cfg.config && typeof cfg.config === 'object' ? cfg.config : {};
  return String(cfg.package ?? inner.package ?? '')
    .toLowerCase()
    .trim();
}

/** When false, tab is visible with package enabled but not auto-added when package is toggled on. */
export function isStandardTabPackageRequired(tab) {
  const cfg = tab?.config;
  if (cfg == null || typeof cfg !== 'object') return true;
  const inner = cfg.config && typeof cfg.config === 'object' ? cfg.config : {};
  if (cfg.package_required === false || inner.package_required === false) return false;
  return true;
}

export function getPackageRequiredObjectNames(standardObjects, enabledPackages) {
  const pkgSet = new Set(
    (enabledPackages || [])
      .map((p) => String(p || '').toLowerCase().trim())
      .filter(Boolean)
  );
  if (pkgSet.size === 0) return [];
  const out = [];
  if (Array.isArray(standardObjects)) {
    for (const o of standardObjects) {
      const pkg = String(o?.config?.package ?? '').toLowerCase().trim();
      if (!pkg || !pkgSet.has(pkg)) continue;
      if (!isStandardObjectPackageRequired(o)) continue;
      const name = String(o?.object_name ?? '').trim();
      if (name) out.push(name);
    }
  }
  const outSet = new Set(out);
  if (pkgSet.has(VISIT_MANAGER_PACKAGE_KEY)) {
    for (const n of VISIT_MANAGER_PACKAGE_OBJECT_NAMES) {
      outSet.add(n);
    }
  }
  if (pkgSet.has(REAL_ESTATE_PACKAGE_KEY)) {
    for (const n of REAL_ESTATE_PACKAGE_OBJECT_NAMES) {
      outSet.add(n);
    }
  }
  return [...outSet];
}

/** Strip allowed object names that belong to a package (used when that package is turned off). */
export function removeAllowedObjectsForPackage(allowedList, standardObjects, packageKey) {
  const pkLc = String(packageKey || '').toLowerCase().trim();
  if (!pkLc || !Array.isArray(allowedList)) return Array.isArray(allowedList) ? [...allowedList] : [];
  const remove = new Set(
    (standardObjects || [])
      .filter((o) => String(o?.config?.package ?? '').toLowerCase().trim() === pkLc)
      .map((o) => String(o?.object_name ?? '').trim())
      .filter(Boolean)
  );
  if (pkLc === VISIT_MANAGER_PACKAGE_KEY) {
    for (const n of VISIT_MANAGER_PACKAGE_OBJECT_NAMES) {
      remove.add(n);
    }
  }
  if (pkLc === REAL_ESTATE_PACKAGE_KEY) {
    for (const n of REAL_ESTATE_PACKAGE_OBJECT_NAMES) {
      remove.add(n);
    }
  }
  if (remove.size === 0) return [...allowedList];
  return allowedList.filter((x) => !remove.has(x));
}

export function normalizePlanConfig(config) {
  const merged = JSON.parse(JSON.stringify(DEFAULT_PLAN_CONFIG));
  if (config && typeof config === 'object') {
    if (Array.isArray(config.allowedObjectsPermission)) {
      merged.allowedObjectsPermission = normalizeAllowedObjects(config.allowedObjectsPermission);
    } else if (Array.isArray(config.objectManager?.allowedObjects)) {
      merged.allowedObjectsPermission = normalizeAllowedObjects(config.objectManager.allowedObjects);
    }
    if (Array.isArray(config.enabledPackages)) {
      merged.enabledPackages = config.enabledPackages;
    }
    Object.keys(merged).forEach((section) => {
      if (section === 'allowedObjectsPermission' || section === 'enabledPackages') return;
      if (config[section] && typeof config[section] === 'object') {
        merged[section] = { ...merged[section], ...config[section] };
      }
    });
    if (merged.leadConversionMapping && (!Array.isArray(merged.leadConversionMapping.allowedObjects) || merged.leadConversionMapping.allowedObjects.length === 0)) {
      merged.leadConversionMapping.allowedObjects = ['account', 'contact', 'opportunity'];
    }
    if (merged.scoringRule && Array.isArray(merged.allowedObjectsPermission) && Array.isArray(merged.scoringRule.allowedObjects)) {
      merged.scoringRule.allowedObjects = merged.scoringRule.allowedObjects.filter((o) => merged.allowedObjectsPermission.includes(o));
    }
    if (merged.assignmentRule && Array.isArray(merged.allowedObjectsPermission) && Array.isArray(merged.assignmentRule.allowedObjects)) {
      merged.assignmentRule.allowedObjects = merged.assignmentRule.allowedObjects.filter((o) => merged.allowedObjectsPermission.includes(o));
    }
    if (merged.webform && Array.isArray(merged.allowedObjectsPermission) && Array.isArray(merged.webform.allowedObjects)) {
      merged.webform.allowedObjects = merged.webform.allowedObjects.filter((o) => merged.allowedObjectsPermission.includes(o));
    }
    // searchView.allowedObjects may include tenant custom objects not listed in allowedObjectsPermission (subscription override).
    if (merged.searchView && Array.isArray(merged.searchView.allowedObjects)) {
      merged.searchView.allowedObjects = filterSearchViewAllowedObjects(merged.searchView.allowedObjects);
    }
  }
  const combinedForCanon = [
    ...(merged.allowedObjectsPermission || []),
    ...(merged.objectManager?.allowedObjects || []),
  ];
  const strippedForCanon = stripDisabledPackageObjects(merged.enabledPackages, combinedForCanon);
  const canonAllowed = mergeAlwaysIncludedAllowedObjects(strippedForCanon);
  merged.allowedObjectsPermission = canonAllowed;
  merged.objectManager = { ...merged.objectManager, allowedObjects: canonAllowed };
  if (merged.scoringRule && Array.isArray(merged.scoringRule.allowedObjects)) {
    merged.scoringRule.allowedObjects = merged.scoringRule.allowedObjects.filter((o) => canonAllowed.includes(o));
  }
  if (merged.assignmentRule && Array.isArray(merged.assignmentRule.allowedObjects)) {
    merged.assignmentRule.allowedObjects = merged.assignmentRule.allowedObjects.filter((o) => canonAllowed.includes(o));
  }
  if (merged.webform && Array.isArray(merged.webform.allowedObjects)) {
    merged.webform.allowedObjects = merged.webform.allowedObjects.filter((o) => canonAllowed.includes(o));
  }
  if (merged.searchView && Array.isArray(merged.searchView.allowedObjects)) {
    merged.searchView.allowedObjects = stripDisabledPackageObjects(
      merged.enabledPackages,
      merged.searchView.allowedObjects
    );
  }
  if (merged.leadConversionMapping && Array.isArray(merged.leadConversionMapping.allowedObjects)) {
    merged.leadConversionMapping.allowedObjects = stripDisabledPackageObjects(
      merged.enabledPackages,
      merged.leadConversionMapping.allowedObjects
    );
  }
  if (Array.isArray(merged.enabledPackages)) {
    merged.enabledPackages = dedupeEnabledPackages(merged.enabledPackages);
  }
  if (merged.storage && config?.storage && typeof config.storage === 'object') {
    const raw = config.storage;
    const data = raw.dataLimitBytes ?? raw.data_limit_bytes;
    const file = raw.fileLimitBytes ?? raw.file_limit_bytes;
    if (data != null && data !== '') {
      const n = Number(data);
      if (Number.isFinite(n)) merged.storage.dataLimitBytes = n;
    }
    if (file != null && file !== '') {
      const n = Number(file);
      if (Number.isFinite(n)) merged.storage.fileLimitBytes = n;
    }
  }
  return merged;
}

/** Standard tab name/object_name values for Report, Dashboard, and Pricebook. These tabs are always shown in Allowed Standard Tabs (not gated by allowedObjectsPermission). */
export const STANDARD_TAB_NAMES_REPORT_DASHBOARD = ['report', 'reports', 'dashboard', 'dashboards', 'pricebook', 'pricebooks'];

/** Returns true if the standard tab is Report, Dashboard, or Pricebook (by name or object_name), so it should always appear in Allowed Standard Tabs for plan_license and company_license. */
export function isReportOrDashboardStandardTab(tab) {
  if (!tab) return false;
  const name = String(tab.name || '').toLowerCase().trim();
  const objName = String(tab.object_name || '').toLowerCase().trim();
  return STANDARD_TAB_NAMES_REPORT_DASHBOARD.includes(name) || STANDARD_TAB_NAMES_REPORT_DASHBOARD.includes(objName);
}

/**
 * Standard tabs that appear in admin "Allowed Standard Tabs" without requiring the object in
 * allowedObjectsPermission (plan/subscription) or objectManager.allowedObjects (license).
 * Task matches product expectation: always available like core CRM chrome.
 */
export function isStandardTabNotGatedByAllowedObjects(tab) {
  if (!tab) return false;
  if (isReportOrDashboardStandardTab(tab)) return true;
  const name = String(tab.name || '').toLowerCase().trim();
  const objName = String(tab.object_name || '').toLowerCase().trim();
  return name === 'task' || objName === 'task';
}

/**
 * When license Object Manager allowed objects change, keep tab UUIDs that must stay
 * (Report, Dashboard, Task) and tabs whose object is still allowed (task ↔ activity).
 */
export function filterLicenseTabIdsForAllowedObjectsChange(tabIds, allowedObjectNames, tabIdToObjectName, unrestrictedTabIds) {
  const allowedSet = new Set((allowedObjectNames || []).map((o) => String(o || '').toLowerCase().trim()).filter(Boolean));
  const unrestricted = unrestrictedTabIds instanceof Set ? unrestrictedTabIds : new Set(unrestrictedTabIds || []);
  return (tabIds || []).filter((tabId) => {
    if (unrestricted.has(tabId)) return true;
    const obj = tabIdToObjectName[tabId];
    if (!obj) return true;
    const objLower = String(obj).toLowerCase().trim();
    if (allowedSet.has(objLower)) return true;
    if (objLower === 'task' && allowedSet.has('activity')) return true;
    return false;
  });
}

/** Empty license tab.allowedTabs means "inherit subscription catalog" — omit key on save. */
export function prepareLicenseEntitlementsForSave(entitlements) {
  if (!entitlements || typeof entitlements !== 'object') return entitlements;
  const out = JSON.parse(JSON.stringify(entitlements));
  if (out.tab && typeof out.tab === 'object' && Array.isArray(out.tab.allowedTabs) && out.tab.allowedTabs.length === 0) {
    const { allowedTabs, ...restTab } = out.tab;
    if (Object.keys(restTab).length > 0) {
      out.tab = restTab;
    } else {
      delete out.tab;
    }
  }
  return out;
}

/** Child/dep object names map to selectable parent. Only parent is stored. Product is selectable independently. */
export const CHILD_TO_PARENT_OBJECT = {
  email_log: 'activity',
  note: 'activity',
  task: 'activity',
  order_product: 'order',
  opportunity_product: 'opportunity',
  product_price: 'opportunity',
  quote_product: 'quote',
};

/** email_log, note, task are part of "activity"; only activity is selectable. */
export const ACTIVITY_GROUP_CHILDREN = ['email_log', 'note', 'task'];

/**
 * Normalize allowed objects: replace children with parent and dedupe.
 * Use when loading or saving allowedObjectsPermission / objectManager.allowedObjects.
 */
export function normalizeAllowedObjects(arr) {
  if (!Array.isArray(arr)) return arr;
  const normalized = arr.map((o) => {
    const k = String(o || '').toLowerCase().trim();
    return CHILD_TO_PARENT_OBJECT[k] || o;
  });
  return normalized.filter((x, i) => normalized.indexOf(x) === i);
}

/** Canonical object names always merged into plan/subscription allowed object lists (stored; children normalize to activity). */
export const ALWAYS_INCLUDED_ALLOWED_OBJECT_NAMES = ['activity', 'attachment'];

/** Merge core engagement objects into an allowed-objects list and normalize. */
export function mergeAlwaysIncludedAllowedObjects(arr) {
  return normalizeAllowedObjects([
    ...new Set([...ALWAYS_INCLUDED_ALLOWED_OBJECT_NAMES, ...(Array.isArray(arr) ? arr : [])]),
  ]);
}

/**
 * Object names that are always migrated; not shown in Allowed Objects picklist (no need to select).
 * user_device migrates with user (backend USER_GROUP).
 * Activity, task, note, email log, and attachment are shown in admin as locked defaults instead.
 * product is not listed — it must appear in plan/subscription/license like other CRM objects (see standard_object).
 * product_price is migrated with opportunity/order/quote via OPPORTUNITY_GROUP, not as a separate picklist item.
 */
export const ALWAYS_MIGRATE_OBJECT_NAMES = ['password_reset', 'role', 'user', 'user_device'];

/** Returns true if object name is always migrated (do not show in allowed-objects selection). */
export function isAlwaysMigrateObject(name) {
  return name && ALWAYS_MIGRATE_OBJECT_NAMES.includes(String(name).toLowerCase().trim());
}

/**
 * Split picklist options into catalog standard vs custom (tenant) objects using standard object keys only.
 * @param {{ key: string, value: string }[]} picklistOptions
 * @param {Set<string>} standardCatalogKeySet keys from the standard catalog picklist (not extras)
 */
export function splitPicklistStandardVsCustom(picklistOptions, standardCatalogKeySet) {
  const standard = [];
  const custom = [];
  for (const o of picklistOptions || []) {
    if (!o || o.key == null) continue;
    if (standardCatalogKeySet.has(o.key)) standard.push(o);
    else custom.push(o);
  }
  return { standard, custom };
}

/**
 * Search Views admin UI: custom objects are those with tenant object_config.is_standard === false.
 * Uses GET /api/admin/company/:tenantCode/objects (same query as listObjectsForTenant).
 * When tenant rows are unavailable (e.g. plan editor), falls back to {@link splitPicklistStandardVsCustom}.
 *
 * @param {{ key: string, value: string }[]} picklistOptions
 * @param {Set<string>} standardCatalogKeySet
 * @param {Array<{ object_name?: string, is_standard?: boolean }>|null|undefined} tenantObjectRows
 */
export function splitSearchViewPicklistByTenantSchema(picklistOptions, standardCatalogKeySet, tenantObjectRows) {
  if (Array.isArray(tenantObjectRows) && tenantObjectRows.length > 0) {
    const customSet = new Set(
      tenantObjectRows
        .filter((r) => r && r.object_name && r.is_standard === false)
        .map((r) => r.object_name)
    );
    const standard = [];
    const custom = [];
    for (const o of picklistOptions || []) {
      if (!o || o.key == null) continue;
      if (customSet.has(o.key)) custom.push(o);
      else standard.push(o);
    }
    return { standard, custom };
  }
  return splitPicklistStandardVsCustom(picklistOptions, standardCatalogKeySet);
}

/**
 * Whether an object key counts as "custom" for Search Views split / merge. Uses tenant schema when provided.
 */
export function isSearchViewCustomObjectKey(key, standardCatalogKeySet, tenantObjectRows) {
  if (!key) return false;
  if (Array.isArray(tenantObjectRows) && tenantObjectRows.length > 0) {
    const row = tenantObjectRows.find((r) => r && r.object_name === key);
    if (row) return row.is_standard === false;
    return !standardCatalogKeySet.has(key);
  }
  return !standardCatalogKeySet.has(key);
}

/**
 * Append tenant custom objects (is_standard === false) to the Search Views custom picklist even if they are not in Allowed Objects Permission.
 */
export function mergeTenantCustomIntoSearchViewPicklist(customPicklistFromSplit, tenantObjectRows) {
  const out = Array.isArray(customPicklistFromSplit) ? [...customPicklistFromSplit] : [];
  const keys = new Set(out.map((o) => o?.key).filter(Boolean));
  for (const r of tenantObjectRows || []) {
    const name = r?.object_name;
    if (!name || r.is_standard !== false) continue;
    if (isAlwaysMigrateObject(name)) continue;
    if (keys.has(name)) continue;
    keys.add(name);
    const label =
      (r.object_label && String(r.object_label).trim()) ||
      String(name).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    out.push({ key: name, value: label });
  }
  out.sort((a, b) => String(a.value || '').localeCompare(String(b.value || '')));
  return out;
}

/** Filter search view allowed object names (exclude objects that should not be configured for search views). */
export function filterSearchViewAllowedObjects(names) {
  if (!Array.isArray(names)) return [];
  return names.filter((n) => {
    const k = String(n || '').toLowerCase().trim();
    if (!k) return false;
    if (isAlwaysMigrateObject(k)) return false;
    return !['activity', 'task', 'note', 'email_log', 'attachment'].includes(k);
  });
}

/** Filter list to exclude always-migrate objects for display in Allowed Objects UI. */
export function allowedObjectsForDisplay(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((o) => !isAlwaysMigrateObject(o));
}

/** Always-migrate objects that can still receive custom fields and belong in admin field migration. */
const FIELD_MIGRATION_ALWAYS_MIGRATE_OBJECTS = new Set(['user', 'user_device', 'role']);

/** Field migration table picker: show CRM objects with custom-field drift, including user/role. */
export function fieldMigrationObjectsForDisplay(list) {
  if (!Array.isArray(list)) return [];
  return list.filter((o) => {
    const k = String(o || '').toLowerCase().trim();
    if (FIELD_MIGRATION_ALWAYS_MIGRATE_OBJECTS.has(k)) return true;
    return !isAlwaysMigrateObject(o);
  });
}

/**
 * Keys shown as selected and locked in admin Allowed Objects (plan/subscription). Task/note/email_log normalize to activity when saved.
 */
export const ADMIN_UI_LOCKED_ALLOWED_OBJECT_KEYS = ['activity', 'task', 'note', 'email_log', 'attachment'];

/** MultiSelect selected values: stored canonical list plus expanded activity-related keys for clear UI. */
export function allowedObjectsSelectedForAdminUi(storedList) {
  const canonical = mergeAlwaysIncludedAllowedObjects(storedList);
  const visible = allowedObjectsForDisplay(canonical);
  return [...new Set([...ADMIN_UI_LOCKED_ALLOWED_OBJECT_KEYS, ...visible])];
}

/** Allowed-objects multiselect: same as allowedObjectsSelectedForAdminUi but hides Visit Manager rows when the package is off. */
export function allowedObjectsSelectedForPlanUi(enabledPackages, storedList) {
  const stripped = stripDisabledPackageObjects(enabledPackages, storedList ?? []);
  return allowedObjectsSelectedForAdminUi(stripped);
}

/** Hide from Search Views "objects allowed" picklist (still valid in CRM; not configured as search-view targets here). */
export function isExcludedFromSearchViewObjectPicklist(name) {
  const k = String(name || '').toLowerCase().trim();
  if (!k) return true;
  if (isAlwaysMigrateObject(k)) return true;
  return ['activity', 'task', 'note', 'email_log', 'attachment'].includes(k);
}

/** All standard objects available for Allowed Objects Permission (user can select which objects to allow). */
export const STANDARD_OBJECT_OPTIONS = [
  { name: 'account', label: 'Account', isStandard: true },
  { name: 'contact', label: 'Contact', isStandard: true },
  { name: 'lead', label: 'Lead', isStandard: true },
  { name: 'opportunity', label: 'Opportunity', isStandard: true },
  { name: 'order', label: 'Order', isStandard: true },
  { name: 'quote', label: 'Quote', isStandard: true },
  { name: 'case', label: 'Case', isStandard: true },
  { name: 'campaign', label: 'Campaign', isStandard: true },
  { name: 'product', label: 'Product', isStandard: true },
  { name: 'asset', label: 'Asset', isStandard: true },
  { name: 'contract', label: 'Contract', isStandard: true },
  { name: 'activity', label: 'Activity', isStandard: true },
  { name: 'task', label: 'Task', isStandard: true },
  { name: 'note', label: 'Note', isStandard: true },
  { name: 'email_log', label: 'Email log', isStandard: true },
  { name: 'attachment', label: 'Attachment', isStandard: true },
  { name: 'event', label: 'Event', isStandard: true },
  { name: 'user', label: 'User', isStandard: true },
  { name: 'report', label: 'Report', isStandard: true },
  { name: 'dashboard', label: 'Dashboard', isStandard: true },
];

export const SETUP_OPTIONS = [
  { key: 'objectmanager', value: 'Object Manager' },
  { key: 'rolemanager', value: 'Role Manager' },
  { key: 'tabs', value: 'Tab Manager' },
  { key: 'appmanager', value: 'App Manager' },
  { key: 'actions', value: 'Action Manager' },
  { key: 'custom-scripts', value: 'Custom Script Studio' },
  { key: 'workflows', value: 'Workflow Manager' },
  { key: 'homewidget', value: 'Edit Home' },
  { key: 'webforms', value: 'Web Forms' },
  { key: 'scoringrule', value: 'Scoring Rules' },
  { key: 'object-health', value: 'Object Health' },
  { key: 'visit-mobile-config', value: 'Visit Mobile Config' },
  { key: 'usergroups', value: 'User Groups' },
  { key: 'users', value: 'Users' },
  { key: 'mailconfig', value: 'Mail Config' },
  { key: 'emailtemplate', value: 'Email Template' },
  { key: 'emaillayout', value: 'Email Layout' },
  { key: 'assignmentrule', value: 'Assignment Rules' },
  { key: 'searchviews', value: 'Search Views' },
  { key: 'session_setting', value: 'Session Settings' },
  { key: 'scheduled-jobs', value: 'Scheduled Jobs' },
  { key: 'currency', value: 'Multi Currency' },
  { key: 'system_settings', value: 'System Settings' },
  { key: 'stage_master', value: 'Probability Manager' },
  { key: 'aichatbot', value: 'AI Chatbot' },
];

/**
 * Default structure for plan_license.entitlements (same shape as plan config).
 * Used to configure per-license restrictions via form, no raw JSON.
 */
export const DEFAULT_ENTITLEMENTS_CONFIG = {
  app: {
    maxApps: -1,
    allowCreateApp: true,
  },
  tab: {
    maxTabs: 5,
    allowCreate: true,
    allowedTabs: [],
  },
  field: {
    maxCustom: 50,
    allowCreate: false,
    allowStandardEdit: true,
    allowStandardDelete: false,
    allowEditLabel: true,
  },
  layout: {
    countMode: 'pair',
    maxLayouts: 1,
    allowCreate: true,
  },
  report: {
    maxReports: 10,
    allowCreateReport: true,
  },
  dashboard: {
    maxDashboards: 5,
    maxAssignedDashboardsPerUser: 2,
    allowCreateDashboard: true,
  },
  workflow: {
    maxWorkflows: 10,
    allowViewWorkflow: true,
    allowCreateWorkflow: true,
  },
  scoringRule: {
    allowViewScoringRule: true,
    allowCreateScoringRule: true,
    allowedObjects: [],
  },
  assignmentRule: {
    allowViewAssignmentRule: true,
    allowCreateAssignmentRule: true,
    allowedObjects: [],
  },
  webform: {
    allowViewWebform: true,
    allowCreateWebform: true,
    allowedObjects: [],
  },
  searchView: {
    allowViewSearchView: true,
    allowCreateSearchView: true,
    allowedObjects: [],
  },
  migration: {
    allow: false,
  },
  objectManager: {
    mode: 'allowlist',
    scope: 'standard',
    allow_create: true,
    enable_standard: true,
    allowedObjects: ['account', 'contact', 'opportunity', 'lead', 'order', 'activity', 'attachment'],
  },
  leadConversionMapping: {
    mode: 'allowlist',
    allow: true,
    allowCustomObject: true,
    scope: 'standard',
    maxObjects: 5,
    allowedObjects: ['account', 'contact', 'opportunity'],
  },
  setup: {
    /** Admin license UI keeps this on; plan/subscription still govern actual app Setup visibility. */
    showSetup: true,
    mode: 'allowlist',
    allowedItems: [
      'objectmanager',
      'rolemanager',
      'tabs',
      'appmanager',
      'actions',
      'custom-scripts',
      'workflows',
      'homewidget',
      'webforms',
      'scoringrule',
      'object-health',
      'usergroups',
      'users',
      'mailconfig',
      'emailtemplate',
      'emaillayout',
      'assignmentrule',
      'searchviews',
      'session_setting',
      'scheduled-jobs',
      'stage_master',
      'aichatbot',
    ],
  },
  /** Per-object permissions (Create, Read, Update, Delete, View All, Modify All). Same UI as role PermissionBulkEdit. */
  objectPermissions: {},
};

export function normalizeEntitlements(entitlements) {
  const merged = JSON.parse(JSON.stringify(DEFAULT_ENTITLEMENTS_CONFIG));
  if (entitlements && typeof entitlements === 'object') {
    Object.keys(merged).forEach((section) => {
      if (entitlements[section] && typeof entitlements[section] === 'object') {
        merged[section] = { ...merged[section], ...entitlements[section] };
      }
    });
  }
  if (merged.setup && typeof merged.setup === 'object') {
    merged.setup.showSetup = true;
  }
  return merged;
}

/**
 * Copy plan config into entitlements shape for new plan_license.
 * When creating a plan license, initial entitlements = exact plan config; user can then configure.
 */
export function entitlementsFromPlanConfig(planConfig) {
  if (!planConfig) return JSON.parse(JSON.stringify(DEFAULT_ENTITLEMENTS_CONFIG));
  const out = JSON.parse(JSON.stringify(DEFAULT_ENTITLEMENTS_CONFIG));
  const sections = ['app', 'tab', 'field', 'layout', 'report', 'dashboard', 'workflow', 'scoringRule', 'assignmentRule', 'webform', 'searchView', 'migration', 'objectManager', 'leadConversionMapping', 'setup', 'objectPermissions'];
  sections.forEach((section) => {
    if (planConfig[section] && typeof planConfig[section] === 'object') {
      out[section] = { ...out[section], ...planConfig[section] };
    }
  });
  if (out.setup && typeof out.setup === 'object') {
    out.setup = { ...out.setup, showSetup: true };
  }
  return out;
}

/**
 * Clamp license entitlements to plan config: booleans/lists cannot exceed the plan.
 * Numeric caps (max tabs, workflows, reports, etc.) are taken only from the plan — plan_license cannot set or lower them.
 * Used when opening Add/Edit plan license and when saving company license entitlements against a subscription template.
 */
export function clampEntitlementsToPlan(entitlements, planConfig) {
  if (!entitlements || !planConfig) return entitlements ? { ...entitlements } : entitlements;
  const out = JSON.parse(JSON.stringify(entitlements));
  const plan = planConfig;

  if (plan.app && out.app) {
    if (plan.app.maxApps != null && plan.app.maxApps !== '') {
      out.app.maxApps = Number(plan.app.maxApps);
    }
    if (!plan.app.allowCreateApp) out.app.allowCreateApp = false;
  }
  if (plan.tab && out.tab) {
    if (plan.tab.maxTabs != null && plan.tab.maxTabs !== '') {
      out.tab.maxTabs = Number(plan.tab.maxTabs);
    }
    if (!plan.tab.allowCreate) out.tab.allowCreate = false;
    const planAllowedTabs = Array.isArray(plan.tab.allowedTabs) ? plan.tab.allowedTabs : [];
    if (planAllowedTabs.length > 0) {
      out.tab.allowedTabs = (out.tab.allowedTabs || []).filter((id) => planAllowedTabs.includes(id));
    }
  }
  if (plan.field && out.field) {
    if (plan.field.maxCustom != null && plan.field.maxCustom !== '') {
      out.field.maxCustom = Number(plan.field.maxCustom);
    }
    if (!plan.field.allowCreate) out.field.allowCreate = false;
    if (!plan.field.allowStandardEdit) out.field.allowStandardEdit = false;
    if (!plan.field.allowStandardDelete) out.field.allowStandardDelete = false;
    if (!plan.field.allowEditLabel) out.field.allowEditLabel = false;
  }
  if (plan.layout && out.layout) {
    if (plan.layout.maxLayouts != null && plan.layout.maxLayouts !== '') {
      out.layout.maxLayouts = Number(plan.layout.maxLayouts);
    }
    if (!plan.layout.allowCreate) out.layout.allowCreate = false;
  }
  if (plan.report && out.report) {
    if (plan.report.maxReports != null && plan.report.maxReports !== '') {
      out.report.maxReports = Number(plan.report.maxReports);
    }
    if (!plan.report.allowCreateReport) out.report.allowCreateReport = false;
  }
  if (plan.dashboard && out.dashboard) {
    if (plan.dashboard.maxDashboards != null && plan.dashboard.maxDashboards !== '') {
      out.dashboard.maxDashboards = Number(plan.dashboard.maxDashboards);
    }
    if (
      plan.dashboard.maxAssignedDashboardsPerUser != null &&
      plan.dashboard.maxAssignedDashboardsPerUser !== ''
    ) {
      out.dashboard.maxAssignedDashboardsPerUser = Number(
        plan.dashboard.maxAssignedDashboardsPerUser
      );
    }
    if (!plan.dashboard.allowCreateDashboard) out.dashboard.allowCreateDashboard = false;
  }
  if (plan.workflow && out.workflow) {
    if (plan.workflow.maxWorkflows != null && plan.workflow.maxWorkflows !== '') {
      out.workflow.maxWorkflows = Number(plan.workflow.maxWorkflows);
    }
    if (!plan.workflow.allowViewWorkflow) out.workflow.allowViewWorkflow = false;
    if (!plan.workflow.allowCreateWorkflow) out.workflow.allowCreateWorkflow = false;
    if (out.workflow.allowCreateWorkflow) out.workflow.allowViewWorkflow = true;
    // Sync with setup.allowedItems: if plan allows 'workflows' in setup, grant at least view
    const planSetupItems = Array.isArray(plan.setup?.allowedItems) ? plan.setup.allowedItems : [];
    if (planSetupItems.map((i) => String(i).toLowerCase()).includes('workflows')) {
      out.workflow.allowViewWorkflow = true;
    }
  }
  if (plan.scoringRule && out.scoringRule) {
    if (!plan.scoringRule.allowViewScoringRule) out.scoringRule.allowViewScoringRule = false;
    if (!plan.scoringRule.allowCreateScoringRule) out.scoringRule.allowCreateScoringRule = false;
    const planScoringObjects = Array.isArray(plan.scoringRule.allowedObjects) ? plan.scoringRule.allowedObjects : [];
    out.scoringRule.allowedObjects = (out.scoringRule.allowedObjects || []).filter((o) => planScoringObjects.includes(o));
  }
  if (plan.assignmentRule && out.assignmentRule) {
    if (!plan.assignmentRule.allowViewAssignmentRule) out.assignmentRule.allowViewAssignmentRule = false;
    if (!plan.assignmentRule.allowCreateAssignmentRule) out.assignmentRule.allowCreateAssignmentRule = false;
    const planAssignmentObjects = Array.isArray(plan.assignmentRule.allowedObjects) ? plan.assignmentRule.allowedObjects : [];
    out.assignmentRule.allowedObjects = (out.assignmentRule.allowedObjects || []).filter((o) => planAssignmentObjects.includes(o));
  }
  if (plan.webform && out.webform) {
    if (!plan.webform.allowViewWebform) out.webform.allowViewWebform = false;
    if (!plan.webform.allowCreateWebform) out.webform.allowCreateWebform = false;
    const planWebformObjects = Array.isArray(plan.webform.allowedObjects) ? plan.webform.allowedObjects : [];
    out.webform.allowedObjects = (out.webform.allowedObjects || []).filter((o) => planWebformObjects.includes(o));
  }
  if (plan.searchView && out.searchView) {
    if (!plan.searchView.allowViewSearchView) out.searchView.allowViewSearchView = false;
    if (!plan.searchView.allowCreateSearchView) out.searchView.allowCreateSearchView = false;
    const planSearchViewObjects = Array.isArray(plan.searchView.allowedObjects) ? plan.searchView.allowedObjects : [];
    out.searchView.allowedObjects = (out.searchView.allowedObjects || []).filter((o) => planSearchViewObjects.includes(o));
  }
  if (plan.migration && out.migration) {
    if (!plan.migration.allow) out.migration.allow = false;
  }
  if (plan.objectManager && out.objectManager) {
    const planObjsRaw = Array.isArray(plan.allowedObjectsPermission)
      ? plan.allowedObjectsPermission
      : Array.isArray(plan.objectManager?.allowedObjects)
        ? plan.objectManager.allowedObjects
        : [];
    const planObjs = mergeAlwaysIncludedAllowedObjects(planObjsRaw);
    out.objectManager.allowedObjects = (out.objectManager.allowedObjects || []).filter((o) => planObjs.includes(o));
    if (!plan.objectManager.allow_create) out.objectManager.allow_create = false;
    if (!plan.objectManager.enable_standard) out.objectManager.enable_standard = false;
  }
  if (plan.leadConversionMapping && out.leadConversionMapping) {
    if (!plan.leadConversionMapping.allow) out.leadConversionMapping.allow = false;
    if (!plan.leadConversionMapping.allowCustomObject) out.leadConversionMapping.allowCustomObject = false;
    if (plan.leadConversionMapping.maxObjects != null && plan.leadConversionMapping.maxObjects !== '') {
      out.leadConversionMapping.maxObjects = Number(plan.leadConversionMapping.maxObjects);
    }
    const planLeadConversionObjs = Array.isArray(plan.leadConversionMapping?.allowedObjects) ? plan.leadConversionMapping.allowedObjects : (Array.isArray(plan.allowedObjectsPermission) ? plan.allowedObjectsPermission : []);
    out.leadConversionMapping.allowedObjects = (out.leadConversionMapping.allowedObjects || []).filter((o) => planLeadConversionObjs.includes(o));
  }
  if (plan.setup && out.setup) {
    out.setup.showSetup = true;
    if (!plan.setup.showSetup) {
      out.setup.allowedItems = [];
    } else {
      const planItems = Array.isArray(plan.setup.allowedItems) ? plan.setup.allowedItems : [];
      out.setup.allowedItems = (out.setup.allowedItems || []).filter((i) => planItems.includes(i));
    }
  }
  if (plan.objectPermissions && typeof plan.objectPermissions === 'object' && out.objectPermissions) {
    const planPerms = plan.objectPermissions;
    const licensePerms = out.objectPermissions || {};
    const clamped = {};
    Object.keys(licensePerms).forEach((objName) => {
      const planObj = planPerms[objName];
      const licObj = licensePerms[objName] || {};
      const keys = ['can_create', 'can_read', 'can_update', 'can_delete', 'view_all', 'modify_all'];
      clamped[objName] = {};
      keys.forEach((k) => {
        if (planObj && typeof planObj === 'object') {
          clamped[objName][k] = Boolean(licObj[k] && planObj[k]);
        } else {
          clamped[objName][k] = Boolean(licObj[k]);
        }
      });
    });
    out.objectPermissions = clamped;
  }
  return out;
}
